import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useGetPickemGameRulesVersionsQuery } from '@/api/pickems/getPickemGameRulesVersions';
import { useUpdatePickemGameRulesVersionMutation } from '@/api/pickems/updatePickemGameRulesVersion';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

interface PickemGameRulesSectionProps {
  disabled?: boolean;
  gameRulesVersionId?: string;
  onGameRulesVersionChange: (versionId: string) => void;
}

export function PickemGameRulesSection({
  disabled = false,
  gameRulesVersionId,
  onGameRulesVersionChange,
}: PickemGameRulesSectionProps) {
  const { data: versionsData, refetch } = useGetPickemGameRulesVersionsQuery('pickem');
  const versions = versionsData?.rules_versions || [];
  const defaultVersion = versions.find((v: any) => v.is_default ?? v.isDefault);

  const [selectedVersionId, setSelectedVersionId] = useState<string>(
    gameRulesVersionId || defaultVersion?.id || ''
  );
  const [editingRulesText, setEditingRulesText] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  const updateMutation = useUpdatePickemGameRulesVersionMutation();

  const getRulesText = (v: any) => (v?.rulesText ?? v?.rules_text ?? '') as string;

  // Initialize selected version and rules text
  useEffect(() => {
    const versionId = gameRulesVersionId || defaultVersion?.id || '';
    if (versionId && versionId !== selectedVersionId) {
      setSelectedVersionId(versionId);
    }
    const selectedVersion = versions.find((v: any) => v.id === versionId);
    if (selectedVersion) {
      setEditingRulesText(getRulesText(selectedVersion));
      if (!gameRulesVersionId && versionId) {
        onGameRulesVersionChange(versionId);
      }
    }
  }, [versions, defaultVersion, gameRulesVersionId]);

  const selectedVersion = versions.find((v: any) => v.id === selectedVersionId);

  const handleVersionChange = (versionId: string) => {
    setSelectedVersionId(versionId);
    onGameRulesVersionChange(versionId);
    const version = versions.find((v: any) => v.id === versionId);
    if (version) {
      setEditingRulesText(getRulesText(version));
      setIsEditing(false);
    }
  };

  const handleStartEditing = () => {
    if (selectedVersion) {
      setEditingRulesText(getRulesText(selectedVersion));
      setIsEditing(true);
    }
  };

  const handleSaveRules = async () => {
    if (!selectedVersionId || !editingRulesText.trim()) {
      toast.error('Rules text cannot be empty');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: selectedVersionId,
        rules_text: editingRulesText.trim(),
      });
      toast.success('Rules updated successfully');
      setIsEditing(false);
      await refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update rules');
    }
  };

  const handleCancelEditing = () => {
    if (selectedVersion) {
      setEditingRulesText(getRulesText(selectedVersion));
      setIsEditing(false);
    }
  };

  if (versions.length === 0) {
    return (
      <div className="space-y-4 border-t pt-6">
        <div>
          <h3 className="text-lg font-semibold mb-2">Game Rules</h3>
          <p className="text-sm text-muted-foreground">
            No game rules versions available. Please create one in Pick'em Settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4 border-t pt-6">
        <div>
          <h3 className="text-lg font-semibold mb-2">Game Rules</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Select a rules version for this contest. You can update the rules text for the selected
            version directly here.
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="game_rules_version_id">Rules Version</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <Select
                  value={selectedVersionId}
                  onValueChange={handleVersionChange}
                  disabled={disabled}
                >
                  <SelectTrigger id="game_rules_version_id">
                    <SelectValue placeholder="Select a rules version" />
                  </SelectTrigger>
                  <SelectContent>
                    {versions.map((v: any) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.title} {(v.is_default ?? v.isDefault) && '(Default)'}{' '}
                        {v.version && `- ${v.version}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TooltipTrigger>
              <TooltipContent>
                Choose which game rules version is shown to users for this contest. Manage versions
                in Pick'em Settings.
              </TooltipContent>
            </Tooltip>
          </div>

          {selectedVersion && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="rules-text">Rules Text</Label>
                {!isEditing && !disabled && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleStartEditing}
                      >
                        Edit Rules
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Edit the rules text for the selected version. Changes apply to all contests
                      using this version.
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              {isEditing ? (
                <div className="space-y-2">
                  <RichTextEditor
                    content={editingRulesText ?? ''}
                    onChange={setEditingRulesText}
                    disabled={updateMutation.isPending}
                  />
                  <div className="flex gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          onClick={handleSaveRules}
                          disabled={updateMutation.isPending || !editingRulesText.trim()}
                          size="sm"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          {updateMutation.isPending ? 'Saving...' : 'Save Rules'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Save the updated rules text to this version. All contests using this version
                        will reflect the change.
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCancelEditing}
                          disabled={updateMutation.isPending}
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Discard changes and return to the read-only view.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              ) : (
                <div
                  className="border rounded-lg p-4 bg-muted/50 prose prose-invert prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: getRulesText(selectedVersion) }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
